import React from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToId } from "@/lib/scroll";
import CalendlyButton from "./CalendlyButton";

const HERO_IMG = "/hero-sad.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-stone-50"
    >
      {/* Image side */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Du dessin à la réalité - agencement et mobilier sur mesure à La Réunion"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-stone-900/30" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-16 pt-32 sm:pb-24">
        <div className="max-w-3xl reveal">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-stone-50/30 bg-stone-50/10 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-stone-50/90 backdrop-blur">
            <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
            Île de la Réunion · 974
          </div>

          <h1 className="text-4xl font-light leading-[1.05] tracking-tighter text-stone-50 sm:text-6xl lg:text-[5.25rem]">
            Agencement intérieur
            <br />
            <span className="italic text-orange-200">&amp; mobilier sur mesure</span>
            <br />
            local.
          </h1>

          <p
            className="mt-8 text-[11px] font-medium uppercase tracking-[0.32em] text-stone-200/95 sm:text-xs"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Conception · Fabrication · Coordination · Installation
          </p>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-200/90 sm:text-lg">
            SAD accompagne particuliers et professionnels dans la création
            d'espaces sur mesure, et propose également du{" "}
            <span className="text-stone-50">mobilier péï</span> conçu localement
            et fabriqué à la demande.
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <a href="#devis" onClick={scrollToId("devis")}>
              <Button
                data-testid="hero-cta-quote"
                size="lg"
                className="group h-12 rounded-full bg-stone-50 px-7 text-stone-900 hover:bg-orange-100"
              >
                Parler de votre projet
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Button>
            </a>
            <CalendlyButton
              variant="dark"
              testId="hero-cta-calendly"
            />
            <a href="#mobilier" onClick={scrollToId("mobilier")}>
              <Button
                data-testid="hero-cta-furniture"
                size="lg"
                variant="ghost"
                className="h-12 rounded-full border border-stone-50/30 px-7 text-stone-50 hover:bg-stone-50/10 hover:text-stone-50"
              >
                Voir le mobilier SAD
              </Button>
            </a>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-16 grid grid-cols-2 gap-4 border-t border-stone-50/15 pt-8 text-stone-50/90 reveal reveal-delay-2 sm:mt-20 sm:grid-cols-4">
          {[
            { k: "100%", v: "Sur-mesure" },
            { k: "974", v: "Fabrication locale" },
            { k: "10+", v: "Artisans partenaires" },
            { k: "0€", v: "Étude & devis" },
          ].map((s) => (
            <div key={s.v}>
              <div className="text-3xl font-light tracking-tight tick">{s.k}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] text-stone-300">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
